import React, { useState, useEffect } from 'react'
import { useTranslation } from '~/hooks/useTranslation'
import { Button, Card, Typography } from '~/components/_atoms'
import { useBenefits } from '../../coach_services/BenefitsProvider'
import { getApi } from '~/util/api'
import ScheduleDialog from '../../coach_services/ProfessionalServices/ScheduleDialog'
import { Stack } from '~/components/_layouts'
import { Calendar } from '~/components/icons/YnsIcon'
import { isEmpty } from 'lodash'
import { CoachSupportCard } from './CoachSupportCard'
import { useFormatTime } from '~/hooks/useFormatTime'

const BaseResumeReviewCard = ({
  className,
  title,
  description,
  actions,
  icon,
}) => {
  const { t } = useTranslation()
  return (
    <Card
      className={className}
      color="secondary"
      actions={
        <div
          className={`grid w-full gap-2 ${actions.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}
        >
          {actions}
        </div>
      }
    >
      <div className="relative">
        {icon ? (
          <div className="float-right mb-4 ml-4">{icon}</div>
        ) : (
          <img
            src="https://www-yns-com.s3.us-east-2.amazonaws.com/images/coach_services/live_chat_network.png"
            alt="Resume Review"
            className="float-right mb-4 ml-4 w-24 @sm:w-32"
          />
        )}
        <Stack spacing={2}>
          <Typography variant="title4" color="secondary" component="h2">
            {title || t('resume_review_card.title')}
          </Typography>
          <Typography>{description}</Typography>
        </Stack>
      </div>
    </Card>
  )
}

const getResumeReviewState = (serviceRequestList, resumeRevisions) => {
  const resumeReviewRequest =
    serviceRequestList?.['ServiceRequest::ResumeReview']

  // service not started
  if (!resumeReviewRequest) {
    return 'NO_REQUEST'
  }
  // done
  if (
    resumeReviewRequest.status === 'closed' ||
    resumeReviewRequest.status === 'revision_declined'
  ) {
    return 'COMPLETED'
  }
  // not pulled off flex queue yet
  if (resumeReviewRequest.status === 'submitted') {
    return 'COACH_NOT_ASSIGNED'
  }
  // candidate chose in-person meeting but first meeting not scheduled
  if (
    resumeReviewRequest.status === 'submitted' &&
    resumeReviewRequest.coach &&
    resumeReviewRequest.meeting_via_calendly
  ) {
    return 'MEETING_NOT_SCHEDULED'
  }

  // scheduled electronic meeting, candidate waiting for revision
  if (
    resumeReviewRequest.status === 'assigned' &&
    resumeReviewRequest.coach &&
    !resumeReviewRequest.meeting_via_calendly
  ) {
    return 'PLATFORM_REVIEW'
  }
  if (
    resumeReviewRequest.status === 'scheduled' &&
    resumeReviewRequest.meeting_via_calendly &&
    resumeReviewRequest.calendly_event?.service_request_no_show_id
  ) {
    return 'MEETING_NOT_SCHEDULED'
  }
  // meeting based, first call
  if (
    resumeReviewRequest.status === 'scheduled' &&
    resumeReviewRequest.meeting_via_calendly &&
    resumeReviewRequest.calendly_event
  ) {
    return 'MEETING_SCHEDULED'
  }
  // first revision is returned to candidate
  if (resumeReviewRequest.status === 'returned' && resumeRevisions.length > 0) {
    return 'RESUME_REVISION'
  }
  // second revision meeting over zoom
  if (resumeReviewRequest.status === 'revision_meeting_requested') {
    return 'REVISION_MEETING_SCHEDULED'
  }
  // this state shows if a revision is requested but no meeting
  // same status whether or not they innitially decided to meet in person
  if (resumeReviewRequest.status === 'revision_requested') {
    return 'REVISION_REQUESTED'
  }
  // second revision is returned to candidate
  if (
    resumeReviewRequest.status === 'final_revision_returned' &&
    resumeRevisions.length > 0
  ) {
    return 'RESUME_REVISION'
  }

  // default state
  return 'MEETING_NOT_SCHEDULED'
}

export function ResumeReviewCard({ className }) {
  const { t, locale } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [serviceRequestList, setServiceRequestList] = useState(null)
  const [resumeRevisions, setResumeRevisions] = useState(null)
  const {
    coach,
    current_user,
    onScheduleClick,
    resumeReview,
    showScheduleDialog,
  } = useBenefits()
  const formatTime = useFormatTime(locale)

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await getApi().get(
          '/coach_services/check_calendly_events',
        )
        if (response.status === 200) {
          const data = response.data
          // Parse the service_requests JSON string
          const parsedServiceRequests = JSON.parse(data.service_requests)
          setServiceRequestList(parsedServiceRequests)

          // Parse the resume_revisions JSON string if it exists
          if (data.resume_revisions) {
            const parsedResumeRevisions = JSON.parse(data.resume_revisions)
            setResumeRevisions(parsedResumeRevisions)
          }
        } else {
          throw new Error('Failed to fetch resume review data')
        }
      } catch (error) {
        console.error('Failed to fetch resume review data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [resumeReview, showScheduleDialog])

  const resumeReviewState = getResumeReviewState(
    serviceRequestList,
    resumeRevisions,
  )

  const renderActions = (resumeReviewState) => {
    const resumeReviewRequest =
      serviceRequestList?.['ServiceRequest::ResumeReview']
    switch (resumeReviewState) {
      case 'PLATFORM_REVIEW':
        return [
          <Button
            key="view"
            color="secondary"
            variant="outline"
            href="/coach_services/resume_review"
            fullWidth
          >
            {t('resume_review_card.view_action')}
          </Button>,
        ]
      case 'WAITING_FOR_REVISION':
        return [
          <Button
            key="view"
            color="secondary"
            variant="outline"
            href="/coach_services/resume_review"
            fullWidth
          >
            {t('resume_review_card.view_action')}
          </Button>,
        ]
      case 'MEETING_SCHEDULED':
        return [
          <Button
            key="join-meeting"
            href={resumeReviewRequest.calendly_event.join_url}
            target="_blank"
            rel="noopener noreferrer"
            variant="solid"
            color="secondary"
          >
            {t(
              'resume_review_card.meeting_with_coach.join_meeting.action_text',
            )}
          </Button>,
          <Button
            key="view"
            color="secondary"
            variant="outline"
            href="/coach_services/resume_review"
            fullWidth
          >
            {t('resume_review_card.view_action')}
          </Button>,
        ]
      case 'REVISION_MEETING_SCHEDULED':
        return [
          <Button
            key="join-meeting"
            href={resumeReviewRequest.calendly_event.join_url}
            target="_blank"
            rel="noopener noreferrer"
            variant="solid"
            color="secondary"
          >
            {t(
              'resume_review_card.meeting_with_coach.join_revision_meeting.action_text',
            )}
          </Button>,
          <Button
            key="view"
            color="secondary"
            variant="outline"
            href="/coach_services/resume_review"
            fullWidth
          >
            {t('resume_review_card.view_action')}
          </Button>,
        ]
      case 'MEETING_NOT_SCHEDULED':
        return [
          <Button
            key="schedule-meeting"
            color="secondary"
            onClick={() => {
              onScheduleClick(
                'ServiceRequest::ResumeReview',
                t(
                  'resume_review_card.meeting_with_coach.schedule_meeting.modal_title',
                ),
                true,
                2,
              )
            }}
            fullWidth
          >
            {t(
              'resume_review_card.meeting_with_coach.schedule_meeting.action_text',
            )}
          </Button>,
          <Button
            key="view-outline"
            color="secondary"
            variant="outline"
            href="/coach_services/resume_review"
            fullWidth
          >
            {t('resume_review_card.view_action')}
          </Button>,
        ]
      // case 'COMPLETED':
      //   return [
      //     <Button
      //       key="chat-with-coach"
      //       color="secondary"
      //       className="open-chat-modal w-full"
      //       data-toggle="modal"
      //       data-target="#coach_chat_list"
      //       data-action={t('coach_support_card.view_action')}
      //       fullWidth
      //     >
      //       {t('resume_review_card.completed.action_text')}
      //     </Button>,
      //   ]
      default:
        return [
          <Button
            key="view"
            color="secondary"
            href="/coach_services/resume_review"
            fullWidth
          >
            {t('resume_review_card.view_action')}
          </Button>,
        ]
    }
  }

  const renderDescription = (state, formatTime) => {
    const resumeReviewRequest =
      serviceRequestList?.['ServiceRequest::ResumeReview']
    const meetingTime = resumeReviewRequest?.calendly_event
      ? formatTime(
          resumeReviewRequest.calendly_event.start_time,
          'meeting_datetime',
        )
      : ''

    switch (state) {
      case 'PLATFORM_REVIEW':
        return t('resume_review_card.platform_review.description', {
          coach_name: `<span class="whitespace-nowrap">${[
            coach?.first_name,
            coach?.last_name,
          ]
            .join(' ')
            .trim()}</span>`,
        })
      case 'WAITING_FOR_REVISION':
        return t(
          'resume_review_card.platform_review.waiting_for_revision.description',
          {
            coach_name: `<span class="whitespace-nowrap">${[coach?.first_name]
              .join(' ')
              .trim()}</span>`,
            update_by: !isEmpty(resumeReviewRequest.assign_resume_review_by_at)
              ? `by ${formatTime(resumeReviewRequest.assign_resume_review_by_at, 'short')}`
              : 'soon',
          },
        )
      case 'RESUME_REVISION':
        return t('resume_review_card.resume_revision.description', {
          coach_name: `<span class="whitespace-nowrap">${[coach?.first_name]
            .join(' ')
            .trim()}</span>`,
        })
      case 'COACH_NOT_ASSIGNED':
        return t('resume_review_card.coach_not_assigned.description', {
          due_at: !isEmpty(resumeReviewRequest.assign_resume_review_by_at)
            ? `by ${formatTime(resumeReviewRequest.assign_resume_review_by_at, 'short')}`
            : 'soon',
        })
      case 'MEETING_SCHEDULED':
        return t(
          'resume_review_card.meeting_with_coach.join_meeting.description',
          {
            meeting_time: meetingTime,
            coach_name: resumeReviewRequest.coach.first_name,
          },
        )
      case 'REVISION_MEETING_SCHEDULED':
        return t(
          'resume_review_card.meeting_with_coach.join_revision_meeting.description',
          {
            meeting_time: meetingTime,
            coach_name: resumeReviewRequest.coach.first_name,
          },
        )
      case 'REVISION_REQUESTED':
        return t(
          'resume_review_card.platform_review.waiting_for_revision.description',
          {
            update_by: formatTime(resumeReviewRequest.due_at, 'short'),
            coach_name: resumeReviewRequest.coach.first_name,
          },
        )
      case 'MEETING_NOT_SCHEDULED':
        return t(
          'resume_review_card.meeting_with_coach.schedule_meeting.description',
          {
            coach_name: resumeReviewRequest.coach.first_name,
          },
        )
      // case 'COMPLETED':
      //   return t('resume_review_card.completed.description')
      default:
        return t('resume_review_card.description')
    }
  }

  const renderIcon = (state) => {
    if (
      state === 'NO_REQUEST' ||
      state === 'RESUME_REVISION' ||
      state === 'COMPLETED'
    )
      return null

    return (
      <Calendar
        className={`h-6 w-6 @sm:h-10 @sm:w-10 ${
          state === 'PLATFORM_REVIEW' ? 'text-intoo-green-medium-700' : ''
        }`}
      />
    )
  }

  const getCardClassName = (state) => {
    switch (state) {
      case 'PLATFORM_REVIEW':
        return `resume-review-platform-review ${className}`
      case 'MEETING_SCHEDULED':
      case 'MEETING_NOT_SCHEDULED':
        return `resume-review-meeting-with-coach ${className}`
      default:
        return `resume-review-choose-type ${className}`
    }
  }

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center p-4">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-secondary"></div>
        </div>
      ) : resumeReviewState === 'COMPLETED' ? (
        <CoachSupportCard />
      ) : (
        <BaseResumeReviewCard
          className={getCardClassName(resumeReviewState)}
          description={renderDescription(resumeReviewState, formatTime)}
          actions={renderActions(resumeReviewState)}
          icon={renderIcon(resumeReviewState)}
        />
      )}
      <ScheduleDialog
        current_user={current_user}
        coach={coach}
        flexResumeReview={true}
      />
    </>
  )
}
